import $ from 'jquery';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';

import ProjectUpdatedAt, {
  PROJECT_UPDATED_AT_HEIGHT,
  PROJECT_UPDATED_AT_MIN_WIDTH,
} from './ProjectUpdatedAt';
import remeasureOnFontsReady from './remeasureOnFontsReady';

class ScriptName extends React.Component {
  static propTypes = {
    name: PropTypes.string.isRequired,
    href: PropTypes.string.isRequired,
    smallText: PropTypes.bool,
    showProjectUpdatedAt: PropTypes.bool,
    width: PropTypes.number,
    setDesiredWidth: PropTypes.func,
    isRtl: PropTypes.bool,
  };

  getFullWidth() {
    const component = $(this.refs.scriptName);
    return component.length > 0 ? component.width() : 0;
  }

  setDesiredWidth() {
    // Skip when truncated: the <a> has width: actualWidth applied, so
    // getFullWidth() would return actualWidth instead of the natural text
    // width, permanently locking the parent's desired-width to the capped value.
    if (!this.props.setDesiredWidth || this._isTruncated) return;
    this.props.setDesiredWidth(this.getFullWidth());
  }

  componentDidMount() {
    this.setDesiredWidth();
    this.cancelFontRemeasure = remeasureOnFontsReady(() =>
      this.setDesiredWidth()
    );
  }

  componentWillUnmount() {
    this.cancelFontRemeasure?.();
  }

  componentDidUpdate() {
    this.setDesiredWidth();
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.props.width !== nextProps.width ||
      this.props.name !== nextProps.name ||
      this.props.showProjectUpdatedAt !== nextProps.showProjectUpdatedAt
    );
  }

  onProjectUpdatedAtContentUpdated = () => {
    this.setDesiredWidth();
  };

  renderScriptLink(linkStyle) {
    let className = 'header_text';
    if (this.props.smallText) {
      className += ' small_font_on_tablet';
    }
    return (
      <a
        href={this.props.href}
        className={className}
        style={linkStyle}
        title={this.props.name}
      >
        {this.props.name}
      </a>
    );
  }

  render() {
    // With no width from the header, the name sits in the header's flex row
    // and ellipsizes whenever the row squeezes it.
    const inFlow = this.props.width === undefined;
    const actualWidth = this.props.width;
    const isTruncated =
      !inFlow && actualWidth > 0 && this.getFullWidth() > actualWidth;
    this._isTruncated = isTruncated;
    const {isRtl} = this.props;

    const ellipsisStyle = inFlow
      ? styles.flowLink
      : isTruncated
      ? {
          display: 'block',
          width: actualWidth,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          ...(isRtl ? {direction: 'rtl'} : {}),
        }
      : {};

    // In flow the two-line layout, its width and its height are kept even
    // before the saved-at state arrives, so nothing moves when it fills in.
    if (!this.props.showProjectUpdatedAt && !inFlow) {
      return (
        <div style={{...styles.headerContainer, height: 18}}>
          <div
            className="script_name"
            ref="scriptName"
            style={styles.headerInner}
          >
            {this.renderScriptLink(ellipsisStyle)}
          </div>
        </div>
      );
    }

    return (
      <div style={inFlow ? styles.flowContainer : styles.headerContainer}>
        <div
          className="script_name"
          ref="scriptName"
          style={
            inFlow ? styles.flowInner : {...styles.headerInner, height: 40}
          }
        >
          <div style={inFlow ? styles.flowOuter : styles.outerContainer}>
            <div
              style={
                inFlow
                  ? styles.flowColumnWithUpdatedAt
                  : styles.containerWithUpdatedAt
              }
            >
              {this.renderScriptLink({
                ...styles.scriptLinkWithUpdatedAt,
                ...ellipsisStyle,
                ...(isTruncated
                  ? {alignSelf: isRtl ? 'flex-end' : 'flex-start'}
                  : {}),
              })}
              {this.props.showProjectUpdatedAt ? (
                <ProjectUpdatedAt
                  onContentUpdated={this.onProjectUpdatedAtContentUpdated}
                />
              ) : (
                <div style={styles.updatedAtPlaceholder} />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const styles = {
  headerContainer: {
    position: 'relative',
    overflow: 'hidden',
    height: 40,
  },
  headerInner: {
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
  },
  flowContainer: {
    height: 40,
    minWidth: 0,
  },
  flowInner: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    minWidth: 0,
  },
  flowOuter: {
    textAlign: 'end',
    minWidth: 0,
  },
  flowLink: {
    display: 'block',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  scriptLinkWithUpdatedAt: {
    display: 'block',
  },
  outerContainer: {
    textAlign: 'end',
  },
  containerWithUpdatedAt: {
    verticalAlign: 'bottom',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
  },
  // The saved-at's width, held before it shows so the row
  // does not re-center when it does. Shrinks with the row like the rest.
  flowColumnWithUpdatedAt: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'end',
    width: PROJECT_UPDATED_AT_MIN_WIDTH,
    maxWidth: '100%',
  },
  // The saved-at's line, held empty until it shows, so the name sits where
  // it will end up.
  updatedAtPlaceholder: {
    height: PROJECT_UPDATED_AT_HEIGHT,
  },
};

export const UnconnectedScriptName = ScriptName;
export default connect(state => ({
  showProjectUpdatedAt: state.project.showProjectUpdatedAt,
}))(ScriptName);
